import { Chip, type EstadoChip } from './Chip';
import { Icono } from './Icono';
import type { NombreIcono } from './iconos';

/**
 * RV Interruptor (componentes.md §9): 48 × 28, bola de 22; encendido rojo, apagado `--rv-rule-strong`; bloqueado negro
 * (o gris) con candado. La fila entera es el botón (alto 56; 64 con ayuda) con el texto a la izquierda. Sin etiqueta,
 * solo el interruptor (zona de 48 × 44) con su `ariaLabel`. `tema="oscuro"` para el widget oscuro (G618): bloqueado y
 * encendido, pista blanca al 55 % y candado tinta.
 */
interface Props {
    etiqueta?: string;
    valor: boolean;
    onChange?: (valor: boolean) => void;
    ayuda?: string;
    error?: string;
    icono?: NombreIcono;
    nota?: string;
    chip?: string;
    chipEstado?: EstadoChip;
    bloqueado?: boolean;
    desactivado?: boolean;
    compacto?: boolean;
    linea?: 'abajo' | 'arriba' | 'ninguna';
    ariaLabel?: string;
    tema?: 'claro' | 'oscuro';
}

export function Interruptor({ etiqueta, valor, onChange, ayuda, error, icono, nota, chip, chipEstado = 'neutral', bloqueado = false, desactivado = false, compacto = false, linea = 'abajo', ariaLabel, tema = 'claro' }: Props) {
    const quieto = bloqueado || desactivado;
    const texto = etiqueta !== undefined && etiqueta !== '';
    const conError = error !== undefined && error !== '';
    const nota2 = conError ? error : (ayuda ?? '');
    const oscuro = tema === 'oscuro';
    const pista = bloqueado ? (valor ? (oscuro ? 'rgba(255,255,255,0.55)' : 'var(--rv-ink)') : 'var(--rv-rule-strong)') : valor ? 'var(--rv-red)' : 'var(--rv-rule-strong)';
    const candado = valor ? (oscuro ? '#111111' : 'var(--rv-ink)') : oscuro ? '#6B6B6B' : 'var(--rv-fg-3)';
    return (
        <button
            type="button"
            role="switch"
            aria-checked={valor}
            aria-disabled={quieto}
            aria-label={ariaLabel}
            className={`rv-int${oscuro ? ' rv-tema-oscuro' : ''}`}
            data-rv-tema={tema}
            style={{
                width: texto ? '100%' : 48,
                minHeight: texto ? (nota2 !== '' || chip !== undefined ? 64 : 56) : 44,
                padding: texto ? '10px 0' : 0,
                borderTop: texto && linea === 'arriba' ? '1px solid var(--rv-rule)' : 'none',
                borderBottom: texto && linea === 'abajo' ? '1px solid var(--rv-rule)' : 'none',
                cursor: quieto ? 'default' : 'pointer',
                opacity: desactivado ? 0.5 : 1,
            }}
            onClick={() => {
                if (!quieto) {
                    onChange?.(!valor);
                }
            }}
        >
            {texto && (
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: compacto ? 14 : 15, fontWeight: compacto ? 400 : 600, lineHeight: 1.4 }}>
                        {icono !== undefined && <Icono nombre={icono} tamano={16} />}
                        <span>{etiqueta}</span>
                    </span>
                    {chip !== undefined && (
                        <span style={{ display: 'flex', marginTop: 2 }}>
                            <Chip estado={chipEstado}>{chip}</Chip>
                        </span>
                    )}
                    {nota2 !== '' && (
                        <span role={conError ? 'alert' : undefined} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: conError ? 12.5 : 13, color: conError ? 'var(--rv-red)' : 'var(--rv-fg-3)', lineHeight: 1.45 }}>
                            {conError && <Icono nombre="circle-alert" tamano={14} />}
                            <span>{nota2}</span>
                        </span>
                    )}
                </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {nota !== undefined && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--rv-fg-3)', whiteSpace: 'nowrap' }}>{nota}</span>}
                <span aria-hidden="true" className="rv-int__pista" style={{ background: pista }}>
                    <span className="rv-int__bola" style={{ transform: valor ? 'translateX(20px)' : 'none' }}>
                        {bloqueado && <Icono nombre="lock" tamano={12} style={{ color: candado }} />}
                    </span>
                </span>
            </span>
        </button>
    );
}
